import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { routineService, exerciseService } from '../services';
import { Eye, Edit, Trash2, Plus } from 'lucide-react';

const DAYS = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'];

const Routines = () => {
  const [routines, setRoutines] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    day_of_week: '',
    exercises: [],
  });

  useEffect(() => {
    loadRoutines();
    loadExercises();
  }, []);

  const loadRoutines = async () => {
    const response = await routineService.getAll();
    setRoutines(response.data);
  };

  const loadExercises = async () => {
    const response = await exerciseService.getAll();
    setExercises(response.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoutine) {
        await routineService.update(editingRoutine.id, formData);
      } else {
        await routineService.create(formData);
      }
      setShowModal(false);
      setEditingRoutine(null);
      setFormData({ name: '', day_of_week: '', exercises: [] });
      loadRoutines();
    } catch (error) {
      alert('Error al guardar la rutina');
    }
  };

  const handleEdit = (routine) => {
    setEditingRoutine(routine);
    setFormData({
      name: routine.name,
      day_of_week: routine.day_of_week,
      exercises: routine.routine_exercises.map(re => ({
        exercise_id: re.exercise_id,
        target_series: re.target_series,
        target_reps: re.target_reps,
        target_weight: re.target_weight || 0,
      })),
    });
    setShowModal(true);
  };

  const handleViewDetails = (routine) => {
    setSelectedRoutine(routine);
    setShowDetailModal(true);
  };

  const addExercise = () => {
    setFormData({
      ...formData,
      exercises: [
        ...formData.exercises,
        { exercise_id: '', target_series: 3, target_reps: 10, target_weight: 0 },
      ],
    });
  };

  const removeExercise = (index) => {
    const newExercises = formData.exercises.filter((_, i) => i !== index);
    setFormData({ ...formData, exercises: newExercises });
  };

  const updateExercise = (index, field, value) => {
    const newExercises = [...formData.exercises];
    newExercises[index][field] = value;
    setFormData({ ...formData, exercises: newExercises });
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta rutina?')) {
      await routineService.delete(id);
      loadRoutines();
    }
  };

  return (
    <Layout>
      <div className="px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Mis Rutinas</h1>
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-cyan-400 to-blue-400 hover:from-cyan-500 hover:to-blue-500 text-gray-900 font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
          >
            <Plus size={20} />
            Nueva Rutina
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {routines.map((routine) => (
            <div key={routine.id} className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold">{routine.name}</h3>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                  {routine.day_of_week}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                💪 {routine.routine_exercises?.length || 0} ejercicios
              </p>
              
              {/* Lista de ejercicios */}
              {routine.routine_exercises?.length > 0 && (
                <div className="mb-3 space-y-1">
                  {routine.routine_exercises.slice(0, 3).map((re, idx) => (
                    <div key={idx} className="text-xs text-gray-700 flex items-center gap-1">
                      <span className="text-gray-400">•</span>
                      <span className="truncate">{re.exercise.name}</span>
                      <span className="text-gray-500">
                        {re.target_series}×{re.target_reps}
                      </span>
                    </div>
                  ))}
                  {routine.routine_exercises.length > 3 && (
                    <p className="text-xs text-gray-500 italic">
                      +{routine.routine_exercises.length - 3} más...
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleViewDetails(routine)}
                  className="bg-green-500 hover:bg-green-600 text-white py-2 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                >
                  <Eye size={14} />
                  Ver
                </button>
                <button
                  onClick={() => handleEdit(routine)}
                  className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                >
                  <Edit size={14} />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(routine.id)}
                  className="bg-red-500 hover:bg-red-600 text-white py-2 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                >
                  <Trash2 size={14} />
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 my-8">
              <h2 className="text-2xl font-bold mb-4">
                {editingRoutine ? '✏️ Editar Rutina' : '➕ Nueva Rutina'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Día de la semana</label>
                  <select
                    required
                    className="w-full px-3 py-2 border rounded-md"
                    value={formData.day_of_week}
                    onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                  >
                    <option value="">Selecciona un día</option>
                    {DAYS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Ejercicios</label>
                    <button
                      type="button"
                      onClick={addExercise}
                      className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Añadir
                    </button>
                  </div>
                  {formData.exercises.map((ex, index) => (
                    <div key={index} className="border p-3 rounded mb-2 space-y-2">
                      <select
                        required
                        className="w-full px-2 py-1 border rounded text-sm"
                        value={ex.exercise_id}
                        onChange={(e) => updateExercise(index, 'exercise_id', e.target.value)}
                      >
                        <option value="">Selecciona ejercicio</option>
                        {exercises.map((exercise) => (
                          <option key={exercise.id} value={exercise.id}>
                            {exercise.name}
                          </option>
                        ))}
                      </select>
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="number"
                          required
                          min="1"
                          placeholder="Series"
                          className="px-2 py-1 border rounded text-sm"
                          value={ex.target_series}
                          onChange={(e) => updateExercise(index, 'target_series', e.target.value)}
                        />
                        <input
                          type="number"
                          required
                          min="1"
                          placeholder="Reps"
                          className="px-2 py-1 border rounded text-sm"
                          value={ex.target_reps}
                          onChange={(e) => updateExercise(index, 'target_reps', e.target.value)}
                        />
                        <input
                          type="number"
                          step="0.5"
                          placeholder="Peso (kg)"
                          className="px-2 py-1 border rounded text-sm"
                          value={ex.target_weight}
                          onChange={(e) => updateExercise(index, 'target_weight', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExercise(index)}
                        className="text-red-500 text-sm"
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingRoutine(null);
                      setFormData({ name: '', day_of_week: '', exercises: [] });
                    }}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 py-2 rounded"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de detalles */}
        {showDetailModal && selectedRoutine && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-8 max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold">{selectedRoutine.name}</h2>
                  <span className="inline-block mt-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {selectedRoutine.day_of_week}
                  </span>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold mb-3">
                  📋 Ejercicios ({selectedRoutine.routine_exercises?.length || 0})
                </h3>
                {selectedRoutine.routine_exercises?.map((re, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {index + 1}. {re.exercise.name}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          🎯 {re.exercise.muscle_group?.name || 'Sin grupo muscular'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                      <div className="bg-white rounded px-3 py-2 border border-gray-200">
                        <p className="text-gray-500 text-xs">Series</p>
                        <p className="font-bold text-lg">{re.target_series}</p>
                      </div>
                      <div className="bg-white rounded px-3 py-2 border border-gray-200">
                        <p className="text-gray-500 text-xs">Repeticiones</p>
                        <p className="font-bold text-lg">{re.target_reps}</p>
                      </div>
                      <div className="bg-white rounded px-3 py-2 border border-gray-200">
                        <p className="text-gray-500 text-xs">Peso (kg)</p>
                        <p className="font-bold text-lg">{re.target_weight || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleEdit(selectedRoutine);
                  }}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <Edit size={20} />
                  Editar Rutina
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 py-3 rounded-lg font-medium"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Routines;
